import React from 'react';
import { useSelector } from 'react-redux';
import { generateEventSourceByPageType } from '../../common/analytics/helpers/analyticsSourceHelper';
import config from '../../common/config';
import { AdUnits } from '../../common/helpers/ads/dfp';
import {
  getAdSectByPageType,
  getAdSubsectByPropertyType,
} from '../../common/helpers/ads/AdsHelper';
import { Content } from '../../common/helpers/data/landingPages/fetchPostsData';
import { fetchPropertyResearchComboLandingPageData } from '../../common/helpers/data/landingPages/fetchPropertyResearchLandingPageData';
import { fetchLandingPageTopContentData } from '../../common/helpers/data/landingPages/fetchTopContentData';
import { getLandingPageType } from '../../common/helpers/domain/landingPages';
import { PostSubcategory } from '../../common/helpers/domain/postCategories';
import {
  getPropertyTypeFromUrlSegment,
  PropertyType,
} from '../../common/helpers/domain/propertyTypes';
import { parsePropertyResearchPostCategory } from '../../common/helpers/routing/landingPageUrlParser';
import { extractPostIds } from '../../components/articleFeed/articleFeedHelper';
import { getNoTabDefinition } from '../../components/articleFeed/articleFeedTab';
import { Breadcrumb } from '../../components/global/breadcrumb/Breadcrumb';
import { PostList } from '../../components/global/PostList/PostList';
import HeroArticle from '../../components/homePage/heroArticle/HeroArticle';
import HomeSeoHeader from '../../components/homePage/homeSeoHeader/HomeSeoHeader';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import { generateComboLandingPageBreadcrumbList } from '../../components/landingPage/breadcrumb';
import BillboardAd from '../../components/landingPageAds/BillboardAd';
import MainTitle from '../../components/mainTitle/MainTitle';
import MostPopularPosts from '../../components/mostPopular/MostPopularPosts';
import {
  DynamicComponentForArticleFeedModule,
  DynamicComponentForFeaturedVideos,
  DynamicComponentForHeroVideo,
  DynamicComponentForInPageSearch,
  DynamicComponentForLocationRecommendation,
} from '../../components/pages/homePage/DynamicComponents';
import {
  AsideContainer,
  LeftContainer,
  TopContentWrapper,
} from '../../components/pages/homePage/HomePage.style';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import Row from '../../components/rows/Row';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import Page from '../../layouts/main';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import commonResource from '../../locales/th/common.json';
import homeResource from '../../locales/th/home.json';
import { HeroArticleInfo } from '../../queries/homePage/heroArticles';
import { HeroVideoInfo } from '../../queries/homePage/heroVideo';
import { RecommendedLocation } from '../../queries/homePage/locationRecommendation';
import { MostPopular } from '../../queries/homePage/mostPopular';
import { updatePageType } from '../../store/home/slice';
import { getComboAnalyticsFeature } from '../../common/analytics/helpers/analyticsFeatureHelper';
import { updateCurrentSourcePage } from '../../store/search/slice';
import { WholeState } from '../../store/common/type';
import {
  trackHeroArticle,
  trackMostPopularArticle,
  trackArticle,
  trackRecommendedLocationCarouselArrow,
  trackRecommendedLocationCard,
  trackHeroVideoArticle,
  trackArticleFeedShowMore,
  trackArticleFeedTab,
} from '../../common/analytics/analyticsTracker';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';

interface PropertyResearchComboLandingPageProps {
  canonicalLink: string;
  postCategory: PostSubcategory.Review | PostSubcategory.Preview;
  propertyType:
    | PropertyType.Condominium
    | PropertyType.House
    | PropertyType.Townhome;
  heroArticle: HeroArticleInfo;
  popularArticles: MostPopular[];
  latestPosts: Content[];
  heroVideo: HeroVideoInfo;
  featuredVideos: Content[];
  recommendedLocations: RecommendedLocation[];
}

function PropertyResearchComboLandingPage({
  postCategory,
  propertyType,
  canonicalLink,
  heroArticle,
  popularArticles,
  latestPosts,
  heroVideo,
  featuredVideos,
  recommendedLocations,
}: PropertyResearchComboLandingPageProps) {
  const moduleResource = commonResource.postModules.propertyResearchCombo;
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );
  const landingPageType = getLandingPageType([postCategory], propertyType);
  const analyticsSource = generateEventSourceByPageType(landingPageType);

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticsSource}
    >
      <HomeSeoHeader pageType={landingPageType} canonicalLink={canonicalLink} />
      <Breadcrumb
        items={generateComboLandingPageBreadcrumbList(
          postCategory,
          propertyType,
        )}
      />
      <Row verticalSpacing={0} maxWidth={homePageRowSetting.maxWidth}>
        <MainTitle
          title={homeResource.title.combo[postCategory]?.[propertyType]}
          subtitle={homeResource.subtitle.combo[postCategory]?.[propertyType]}
        />
      </Row>
      <Row
        verticalSpacing={0}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <TopContentWrapper>
          <LeftContainer>
            <HeroArticle
              heroArticleData={heroArticle}
              onClickHeroArticle={(heroArticle) =>
                trackHeroArticle(
                  userInfo,
                  heroArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_ARTICLE,
                )
              }
            />
          </LeftContainer>
          <AsideContainer>
            <MostPopularPosts
              post={popularArticles}
              onClick={(popularArticle) =>
                trackMostPopularArticle(
                  userInfo,
                  popularArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.POPULAR,
                )
              }
            />
          </AsideContainer>
        </TopContentWrapper>
      </Row>
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard1}
          sect={getAdSectByPageType(landingPageType)}
          subsect={getAdSubsectByPropertyType(propertyType)}
        />
      </Row>
      {latestPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="review"
        >
          <ModuleTitle
            title={moduleResource[postCategory]?.[propertyType]?.title}
            subtitle={moduleResource[postCategory]?.[propertyType]?.subtitle}
          />
          <PostList
            contents={latestPosts}
            adUnit={AdUnits.HP_Pos1}
            adSect={getAdSectByPageType(landingPageType)}
            adSubsect={getAdSubsectByPropertyType(propertyType)}
            onClickPostCard={(post) =>
              trackArticle(
                userInfo,
                post,
                analyticsSource,
                getComboAnalyticsFeature({
                  postSubCategory: postCategory,
                  propertyType,
                }),
              )
            }
          />
        </Row>
      )}
      {recommendedLocations.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <ModuleTitle
            title={homeResource.locationRecommendation.title}
            subtitle={homeResource.locationRecommendation.subtitle}
          />
          <DynamicComponentForLocationRecommendation
            locations={recommendedLocations}
            onClickRecommendedLocationCarouselArrow={() =>
              trackRecommendedLocationCarouselArrow(
                userInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_HIGHLIGHT,
              )
            }
            onClickRecommendedLocationCard={(locationInfo) =>
              trackRecommendedLocationCard(
                userInfo,
                locationInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_TRAINELINE_HIGHLIGHT,
              )
            }
          />
        </Row>
      )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <DynamicComponentForInPageSearch />
      </Row>
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <BillboardAd
          adUnit={AdUnits.HP_Billboard2}
          sect={getAdSectByPageType(landingPageType)}
          subsect={getAdSubsectByPropertyType(propertyType)}
        />
      </Row>
      {postCategory === PostSubcategory.Review &&
        (heroVideo || featuredVideos.length > 0) && (
          <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
            <div style={{ overflowX: 'hidden' }}>
              <DynamicComponentForHeroVideo
                heroVideo={heroVideo}
                onClick={(heroVideo) =>
                  trackHeroVideoArticle(
                    userInfo,
                    heroVideo,
                    analyticsSource,
                    ANALYTICS_FEATURES.HERO_VIDEO,
                  )
                }
              />
              <DynamicComponentForFeaturedVideos
                contents={featuredVideos}
                onClickVideoCard={(post) =>
                  trackArticle(
                    userInfo,
                    post,
                    analyticsSource,
                    ANALYTICS_FEATURES.VIDEO,
                  )
                }
              />
            </div>
          </Row>
        )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <ModuleTitle title={homeResource.readMore.title} />
        <DynamicComponentForArticleFeedModule
          tabDefinition={getNoTabDefinition(propertyType, [postCategory])}
          excludePostIds={extractPostIds(
            [...latestPosts, ...featuredVideos],
            heroVideo,
          )}
          adUnit={AdUnits.HP_MrecInfiniteAd}
          adSect={getAdSectByPageType(landingPageType)}
          adSubsect={getAdSubsectByPropertyType(propertyType)}
          onClickPostCard={(post) =>
            trackArticle(
              userInfo,
              post,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            )
          }
          onClickArticleFeedShowMore={() => {
            trackArticleFeedShowMore(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            );
          }}
          onClickArticleFeedTab={(selectedTab) => {
            trackArticleFeedTab(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
              selectedTab,
            );
          }}
        />
      </Row>
      <Row verticalSpacing={16} fullWidth>
        <SeoFooter />
      </Row>
    </Page>
  );
}

PropertyResearchComboLandingPage.getInitialProps = async ({
  store,
  asPath,
  apolloClient,
  query,
}: CustomNextPageContext): Promise<PropertyResearchComboLandingPageProps> => {
  const canonicalLink = config.baseUrl + asPath.substring(1);
  const propertyType = getPropertyTypeFromUrlSegment(
    query.propertyType as string,
  ) as PropertyType.Condominium | PropertyType.House | PropertyType.Townhome;
  const postCategory = parsePropertyResearchPostCategory(
    query.category as string,
  ) as PostSubcategory.Review | PostSubcategory.Preview;
  try {
    const { heroArticle, mostPopular, recommendedLocations } =
      await fetchLandingPageTopContentData(apolloClient, {
        postCategories: [postCategory],
        propertyType,
      });

    const { latest, video } = await fetchPropertyResearchComboLandingPageData(
      apolloClient,
      postCategory,
      propertyType,
      heroArticle?.id ? [heroArticle.id] : [],
    );

    const landingPageType = getLandingPageType([postCategory], propertyType);
    store.dispatch(updatePageType(landingPageType));
    store.dispatch(
      updateCurrentSourcePage(generateEventSourceByPageType(landingPageType)),
    );

    return {
      postCategory,
      propertyType,
      canonicalLink,
      heroArticle,
      popularArticles: mostPopular,
      latestPosts: latest,
      heroVideo: video.heroVideo,
      featuredVideos: video.featuredVideos,
      recommendedLocations,
    };
  } catch (e) {
    console.log(
      'Error when fetching posts for property research combo landing page :',
      e,
    );
  }
};

export default PropertyResearchComboLandingPage;
